import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const BlogModeration = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [actioning, setActioning] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    const params = filter === 'all' ? {} : { status: filter };
    api.get('/api/blog/admin/pending', { params })
      .then(res => {
        setPosts(res.data.posts || []);
      })
      .catch(err => {
        if (err.response?.status === 403) {
          setError('You do not have permission to moderate blog posts.');
        } else {
          setError(err.response?.data?.error || 'Failed to load posts');
        }
        setPosts([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filter]);

  const handleApprove = async (id) => {
    setActioning(id);
    try {
      await api.patch(`/api/blog/admin/${id}/approve`);
      setPosts(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return; // cancelled
    setActioning(id);
    try {
      await api.patch(`/api/blog/admin/${id}/reject`, { reason: reason || undefined });
      setPosts(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to reject');
    } finally {
      setActioning(null);
    }
  };

  const handleUnpublish = async (id) => {
    if (!window.confirm('Remove this post from the blog? It will no longer be visible to visitors.')) return;
    setActioning(id);
    try {
      await api.patch(`/api/blog/admin/${id}/unpublish`);
      setPosts(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Failed to unpublish');
    } finally {
      setActioning(null);
    }
  };

  const pendingCount = filter === 'pending' ? posts.length : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-900">Blog moderation</h1>
        <div className="flex gap-2">
          {['pending', 'published', 'rejected', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                filter === s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <p className="text-gray-500">No posts in this category.</p>
      )}

      {!loading && !error && posts.length > 0 && (
        <ul className="space-y-3">
          {posts.map(post => (
            <li
              key={post._id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">{post.title}</p>
                <p className="text-sm text-gray-500">
                  {post.authorName}
                  {post.createdAt && ` · ${new Date(post.createdAt).toLocaleDateString()}`}
                  {post.status !== 'pending' && ` · ${post.status}`}
                </p>
                {post.body && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{post.body.slice(0, 150)}…</p>
                )}
              </div>
              {post.status === 'pending' && (
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleApprove(post._id)}
                    disabled={actioning === post._id}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(post._id)}
                    disabled={actioning === post._id}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              )}
              {post.status === 'published' && (
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleUnpublish(post._id)}
                    disabled={actioning === post._id}
                    className="px-3 py-1.5 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                  >
                    Unpublish
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BlogModeration;
